import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  DocumentIcon,
  UserIcon,
  ArrowPathIcon,
  EyeIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import {
  fetchCountries,
  fetchPurseOptions,
  fetchTrainingDisciplines,
  fetchWeightClasses,
  getConfirmedDeals,
} from "../../services/api";
import { DealResponseDto } from "../../types/types";
import { formatDate } from "../../utils/common/formatUtils";
import {
  formatSportName,
  formatWeightClassName,
} from "../../utils/utilMethods";
import { useNavigate } from "react-router-dom";
import noDealsEmptyIcon from "../../assets/icons/noDealsEmpty.svg";
import { SelectItem, Spinner } from "@heroui/react";
import AppSelect from "../../components/common/appSelect";

const getSport = (deal: DealResponseDto): string =>
  deal.formData?.details?.sport ?? deal.formData?.sport ?? "—";

const getWeightClass = (deal: DealResponseDto): string =>
  deal.formData?.details?.weightClass ?? deal.formData?.weightClass ?? "—";

const getLocation = (deal: DealResponseDto): string => {
  const loc = deal.formData?.details?.location ?? deal.formData?.location;
  if (!loc) return "—";
  if (typeof loc === "object") {
    return [loc.city, loc.country].filter(Boolean).join(", ");
  }
  return String(loc);
};

const getEventDate = (deal: DealResponseDto): string =>
  deal.formData?.details?.eventDate ?? deal.formData?.eventDate ?? "";

const formatDealTypeLabel = (value?: string): string =>
  String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const dealStatusOptions = [
  "ACCEPTED",
  "CONFIRMED",
  "WAITING_FOR_RESULT",
  "RESULT_UPDATED",
  "RESULT_CONFIRMED",
  "FIGHTER_RATED",
  "MANAGER_RATED",
  "FINISHED",
];

const pageSizeOptions = [10, 20, 50];

const formatStatusLabel = (status: string): string =>
  String(status || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const DealAccordion: React.FC = () => {
  const { t } = useTranslation("dealAccordion");
  const navigate = useNavigate();

  const [dealRequests, setDealRequests] = useState<DealResponseDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<string>("");
  const [dealTypeFilter, setDealTypeFilter] = useState<string>("");
  const [sportFilter, setSportFilter] = useState<string>("");
  const [weightClassFilter, setWeightClassFilter] = useState<string>("");
  const [locationFilter, setLocationFilter] = useState<string>("");
  const [shortNoticeFilter, setShortNoticeFilter] = useState<boolean>(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState<boolean>(false);

  const [weightClassList, setWeightClassList] = useState<string[]>([]);
  const [disciplineList, setDisciplineList] = useState<string[]>([]);
  const [purseList, setPurseList] = useState<string[]>([]);
  const [countryList, setCountryList] = useState<string[]>([]);

  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(15);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const pageRef = React.useRef(0);
  const observerRef = React.useRef<HTMLDivElement | null>(null);

  const activeFilterCount =
    (statusFilter ? 1 : 0) +
    (dealTypeFilter ? 1 : 0) +
    (sportFilter ? 1 : 0) +
    (weightClassFilter ? 1 : 0) +
    (locationFilter ? 1 : 0) +
    (shortNoticeFilter ? 1 : 0);

  useEffect(() => {
    fetchWeightClasses()
      .then((data: string[]) => {
        if (Array.isArray(data)) setWeightClassList(data);
      })
      .catch(() => {});

    fetchTrainingDisciplines()
      .then((data: string[]) => {
        if (Array.isArray(data)) setDisciplineList(data);
      })
      .catch(() => {});

    fetchPurseOptions()
      .then((data: string[]) => {
        if (Array.isArray(data)) setPurseList(data);
      })
      .catch(() => {});

    fetchCountries()
      .then((data: string[]) => {
        if (Array.isArray(data)) setCountryList(data);
      })
      .catch(() => {});
  }, []); 

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    setError(null);
    pageRef.current = 0;
    try {
      const data = await getConfirmedDeals(
        statusFilter || undefined,
        undefined,
        undefined,
        0,
        size,
      );
      setDealRequests(data);
      setPage(0);
      setHasMore(data.length >= size);
    } catch (err) {
      console.error(err);
      setError(t("errorFetch", "Failed to load deals"));
    } finally {
      setLoading(false);
    }
  }, [size, statusFilter, t]);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    const nextPage = pageRef.current + 1;
    setLoadingMore(true);
    try {
      const data = await getConfirmedDeals(
        statusFilter || undefined,
        undefined,
        undefined,
        nextPage,
        size,
      );
      if (data && data.length > 0) {
        pageRef.current = nextPage;
        setPage(nextPage);
        setDealRequests((prev) => {
          const existingIds = new Set(prev.map((d) => d.id));
          const newItems = data.filter((d) => !existingIds.has(d.id));
          return [...prev, ...newItems];
        });
        setHasMore(data.length >= size);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error(err);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loading, loadingMore, size, statusFilter]);

  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;
    const target = observerRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { root: null, rootMargin: "300px", threshold: 0 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [loading, loadingMore, hasMore, loadMore]);

  const getUserRole = (): string => {
    const userJson = localStorage.getItem("loginResponse");
    const user = userJson ? JSON.parse(userJson) : null;
    return user?.profileType || "";
  };

  const role = getUserRole();

  const getStatusLabel = useCallback(
    (status: string): string => {
      if (!status) return "";
      return formatStatusLabel(status);
    },
    [],
  );

  const resetFilters = () => {
    setStatusFilter("");
    setDealTypeFilter("");
    setSportFilter("");
    setWeightClassFilter("");
    setLocationFilter("");
    setShortNoticeFilter(false);
    setPage(0);
    setSize(10);
  };

  const navigateToProfile = (profileId?: number | string | null) => {
    if (!profileId) return;
    navigate(`/profile?profileId=${profileId}`);
  };

  const filteredDeals = useMemo(() => {
    return dealRequests.filter((deal) => {
      if (dealTypeFilter && deal.dealType !== dealTypeFilter) return false;
      if (
        sportFilter &&
        !getSport(deal).toLowerCase().includes(sportFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        weightClassFilter &&
        !getWeightClass(deal)
          .toLowerCase()
          .includes(weightClassFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        locationFilter &&
        !getLocation(deal)
          .toLowerCase()
          .includes(locationFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        shortNoticeFilter &&
        !(deal.formData?.details?.shortNotice || (deal as { shortNotice?: boolean }).shortNotice)
      ) {
        return false;
      }
      return true;
    });
  }, [
    dealRequests,
    dealTypeFilter,
    sportFilter,
    weightClassFilter,
    locationFilter,
    shortNoticeFilter,
  ]);

  const getStatusBadgeStyle = (status?: string) => {
    const s = String(status || "").toUpperCase();
    if (s.includes("CONFIRMED") || s.includes("FINISHED")) {
      return "bg-[#dcfce7] text-[#15803d]";
    }
    if (s.includes("ACCEPTED")) {
      return "bg-[#dbeafe] text-[#1d4ed8]";
    }
    if (s.includes("WAITING")) {
      return "bg-[#fef9c3] text-[#a16207]";
    }
    if (s.includes("CANCEL") || s.includes("REJECT") || s.includes("DISPUTE")) {
      return "bg-[#fee2e2] text-[#b91c1c]";
    }
    return "bg-[#fed7aa] text-[#c2410c]";
  };

  return (
    <div className="w-full bg-[#f8fafc] min-h-screen font-['Plus_Jakarta_Sans'] pb-12">
      <div className="hidden lg:block w-full bg-white border-b border-slate-200/80 px-4 sm:px-6 py-4 mb-6">
        <div className="max-w-full mx-auto flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <h1 className="text-xl font-bold text-gray-900 font-['Plus_Jakarta_Sans'] shrink-0">My Deals</h1>
          <div className="flex flex-wrap items-center gap-2.5">
            <AppSelect
              aria-label={t("all", "All")}
              placeholder={t("all", "All")}
              selectedKeys={statusFilter ? [statusFilter] : []}
              onSelectionChange={(keys) => {
                setStatusFilter((Array.from(keys)[0] as string) || "");
                setPage(0);
              }}
              size="sm"
              radius="md"
              className="w-48"
              classNames={{
                trigger: "h-9 bg-white border border-gray-200 shadow-none",
                value: "text-sm font-normal leading-5 text-gray-700",
              }}
            >
              {dealStatusOptions.map((status) => (
                <SelectItem key={status} textValue={getStatusLabel(status)}>
                  {getStatusLabel(status)}
                </SelectItem>
              ))}
            </AppSelect>

            <AppSelect
              aria-label="Purse options"
              placeholder="Purse options"
              selectedKeys={dealTypeFilter ? [dealTypeFilter] : []}
              onSelectionChange={(keys) =>
                setDealTypeFilter((Array.from(keys)[0] as string) || "")
              }
              size="sm"
              radius="md"
              className="w-48"
              classNames={{
                trigger: "h-9 bg-white border border-gray-200 shadow-none",
                value: "text-sm font-normal leading-5 text-gray-700",
              }}
            >
              {purseList.length > 0
                ? purseList.map((purse) => (
                    <SelectItem key={purse} textValue={formatDealTypeLabel(purse)}>
                      {formatDealTypeLabel(purse)}
                    </SelectItem>
                  ))
                : [
                    { key: "FIGHT_SINGLE", label: "Fight single" },
                    { key: "FIGHT_TOURNAMENT", label: "Fight tournament" },
                    { key: "SPARRING", label: "Sparring" },
                  ].map((opt) => (
                    <SelectItem key={opt.key} textValue={opt.label}>
                      {opt.label}
                    </SelectItem>
                  ))}
            </AppSelect>

            <AppSelect
              aria-label="Fighting discipline"
              placeholder="Fighting discipline"
              selectedKeys={sportFilter ? [sportFilter] : []}
              onSelectionChange={(keys) =>
                setSportFilter((Array.from(keys)[0] as string) || "")
              }
              size="sm"
              radius="md"
              className="w-48"
              classNames={{
                trigger: "h-9 bg-white border border-gray-200 shadow-none",
                value: "text-sm font-normal leading-5 text-gray-700",
              }}
            >
              {(disciplineList.length > 0
                ? disciplineList
                : ["MMA", "BOXING", "MUAY_THAI", "KICKBOXING", "BJJ"]
              ).map((disc) => (
                <SelectItem key={disc} textValue={formatSportName(disc)}>
                  {formatSportName(disc)}
                </SelectItem>
              ))}
            </AppSelect>

            <AppSelect
              aria-label="Weight class"
              placeholder="Weight class"
              selectedKeys={weightClassFilter ? [weightClassFilter] : []}
              onSelectionChange={(keys) =>
                setWeightClassFilter((Array.from(keys)[0] as string) || "")
              }
              size="sm"
              radius="md"
              className="w-40"
              classNames={{
                trigger: "h-9 bg-white border border-gray-200 shadow-none",
                value: "text-sm font-normal leading-5 text-gray-700",
              }}
            >
              {weightClassList.map((wc) => (
                <SelectItem key={wc} textValue={formatWeightClassName(wc)}>
                  {formatWeightClassName(wc)}
                </SelectItem>
              ))}
            </AppSelect>

            <AppSelect
              aria-label="Location"
              placeholder="Location"
              selectedKeys={locationFilter ? [locationFilter] : []}
              onSelectionChange={(keys) =>
                setLocationFilter((Array.from(keys)[0] as string) || "")
              }
              size="sm"
              radius="md"
              className="w-64"
              classNames={{
                trigger: "h-9 bg-white border border-gray-200 shadow-none",
                value: "text-sm font-normal leading-5 text-gray-700",
              }}
            >
              {countryList.map((country) => (
                <SelectItem key={country} textValue={country}>
                  {country}
                </SelectItem>
              ))}
            </AppSelect>
            <div
              onClick={() => setShortNoticeFilter(!shortNoticeFilter)}
              className="flex items-center gap-3 cursor-pointer select-none pl-1"
            >
              <div
                className={`w-12 h-6 p-0.5 rounded-3xl transition-colors ${
                  shortNoticeFilter ? "bg-blue-600" : "bg-slate-200"
                }`}
              >
                <div
                  className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                    shortNoticeFilter ? "translate-x-6" : "translate-x-0"
                  }`}
                />
              </div>
              <span className="text-base font-medium text-slate-900 font-['Plus_Jakarta_Sans'] leading-6 whitespace-nowrap">
                Short notice
              </span>
            </div>

            {(statusFilter ||
              dealTypeFilter ||
              sportFilter ||
              weightClassFilter ||
              locationFilter ||
              shortNoticeFilter) && (
              <button
                type="button"
                onClick={resetFilters}
                className="p-1.5 rounded-xl text-slate-500 hover:bg-slate-100 transition-colors"
                title={t("resetFilters", "Reset filters")}
              >
                <ArrowPathIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="lg:hidden flex items-center justify-between px-4 pt-4 pb-2 bg-[#f8fafc]">
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            className="appearance-none bg-slate-100 hover:bg-slate-200/80 border-none rounded-2xl pl-4 pr-9 py-2 text-sm font-medium text-slate-700 cursor-pointer outline-none transition-colors"
          >
            <option value="">{t("all", "All")}</option>
            {dealStatusOptions.map((status) => (
              <option key={status} value={status}>
                {getStatusLabel(status)}
              </option>
            ))}
          </select>
          <ChevronDownIcon className="w-4 h-4 text-slate-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>

        <button
          type="button"
          onClick={() => setIsFilterDrawerOpen(true)}
          className="size-9 min-w-9 rounded-2xl bg-white border border-slate-200/80 shadow-sm flex items-center justify-center text-slate-700 hover:bg-slate-50 transition-colors relative shrink-0"
          aria-label="Filter"
        >
          <FunnelIcon className="w-4 h-4 text-slate-700" />
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 size-4 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {isFilterDrawerOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center lg:hidden">
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity"
            onClick={() => setIsFilterDrawerOpen(false)}
          />
          <div className="relative w-full max-h-[85vh] overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl flex flex-col gap-4 z-10">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
                  {t("filters", "Filters")}
                </h2>
                {activeFilterCount > 0 && (
                  <span className="size-5 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setIsFilterDrawerOpen(false)}
                className="size-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors"
                aria-label="Close filters"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="flex flex-col gap-3 font-['Plus_Jakarta_Sans']">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  className="w-full h-10 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-normal text-slate-700 outline-none focus:border-blue-400 focus:bg-white cursor-pointer"
                >
                  <option value="">{t("all", "All")}</option>
                  {dealStatusOptions.map((status) => (
                    <option key={status} value={status}>
                      {getStatusLabel(status)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Purse options</label>
                <select
                  value={dealTypeFilter}
                  onChange={(e) => setDealTypeFilter(e.target.value)}
                  className="w-full h-10 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-normal text-slate-700 outline-none focus:border-blue-400 focus:bg-white cursor-pointer"
                >
                  <option value="">Purse options</option>
                  {purseList.length > 0
                    ? purseList.map((purse) => (
                        <option key={purse} value={purse}>
                          {formatDealTypeLabel(purse)}
                        </option>
                      ))
                    : [
                        { key: "FIGHT_SINGLE", label: "Fight single" },
                        { key: "FIGHT_TOURNAMENT", label: "Fight tournament" },
                        { key: "SPARRING", label: "Sparring" },
                      ].map((opt) => (
                        <option key={opt.key} value={opt.key}>
                          {opt.label}
                        </option>
                      ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Fighting discipline</label>
                <select
                  value={sportFilter}
                  onChange={(e) => setSportFilter(e.target.value)}
                  className="w-full h-10 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-normal text-slate-700 outline-none focus:border-blue-400 focus:bg-white cursor-pointer"
                >
                  <option value="">Fighting discipline</option>
                  {disciplineList.length > 0
                    ? disciplineList.map((disc) => (
                        <option key={disc} value={disc}>
                          {formatSportName(disc)}
                        </option>
                      ))
                    : ["MMA", "BOXING", "MUAY_THAI", "KICKBOXING", "BJJ"].map((d) => (
                        <option key={d} value={d}>
                          {formatSportName(d)}
                        </option>
                      ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Weight class</label>
                {weightClassList.length > 0 ? (
                  <select
                    value={weightClassFilter}
                    onChange={(e) => setWeightClassFilter(e.target.value)}
                    className="w-full h-10 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-normal text-slate-700 outline-none focus:border-blue-400 focus:bg-white cursor-pointer"
                  >
                    <option value="">Weight class</option>
                    {weightClassList.map((wc) => (
                      <option key={wc} value={wc}>
                        {formatWeightClassName(wc)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    placeholder="Weight class"
                    value={weightClassFilter}
                    onChange={(e) => setWeightClassFilter(e.target.value)}
                    className="w-full h-10 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-normal text-slate-700 outline-none placeholder-slate-400 focus:border-blue-400 focus:bg-white"
                  />
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Location</label>
                {countryList.length > 0 ? (
                  <select
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="w-full h-10 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-normal text-slate-700 outline-none focus:border-blue-400 focus:bg-white cursor-pointer"
                  >
                    <option value="">Location</option>
                    {countryList.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    placeholder="Location"
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="w-full h-10 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-normal text-slate-700 outline-none placeholder-slate-400 focus:border-blue-400 focus:bg-white"
                  />
                )}
              </div>

              <div
                onClick={() => setShortNoticeFilter(!shortNoticeFilter)}
                className="flex items-center justify-between py-2 cursor-pointer select-none"
              >
                <span className="text-sm font-medium text-slate-900">
                  Short notice
                </span>
                <div
                  className={`w-12 h-6 p-0.5 rounded-3xl transition-colors ${
                    shortNoticeFilter ? "bg-blue-600" : "bg-slate-200"
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                      shortNoticeFilter ? "translate-x-6" : "translate-x-0"
                    }`}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => {
                  resetFilters();
                  setIsFilterDrawerOpen(false);
                }}
                className="flex-1 py-2.5 px-4 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                {t("resetFilters", "Reset filters")}
              </button>
              <button
                type="button"
                onClick={() => setIsFilterDrawerOpen(false)}
                className="flex-1 py-2.5 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-sm font-medium text-white transition-colors"
              >
                {t("apply", "Apply")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="px-4 sm:px-6">
        {loading && (
          <>
            <div className="lg:hidden flex flex-col gap-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="bg-white rounded-3xl p-4 border border-slate-200/80 shadow-sm animate-pulse flex flex-col gap-3"
                >
                  <div className="h-3 w-16 bg-slate-200 rounded" />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-full bg-slate-200" />
                      <div className="space-y-1.5">
                        <div className="h-4 w-28 bg-slate-200 rounded" />
                        <div className="h-3 w-20 bg-slate-100 rounded" />
                      </div>
                    </div>
                    <div className="h-6 w-20 bg-slate-200 rounded-xl" />
                  </div>
                  <div className="space-y-2.5 pt-2 border-t border-slate-100">
                    <div className="h-4 bg-slate-100 rounded w-full" />
                    <div className="h-4 bg-slate-100 rounded w-full" />
                    <div className="h-4 bg-slate-100 rounded w-full" />
                  </div>
                  <div className="h-10 bg-slate-100 rounded-2xl w-full mt-1" />
                </div>
              ))}
            </div>

            <div className="hidden lg:block p-1 bg-[#f4f5f7] rounded-[20px] border border-slate-200/80 overflow-hidden shadow-sm animate-pulse">
              <div className="w-full overflow-x-auto">
                <div className="min-w-[1000px] px-4 py-2.5 flex items-center gap-6 text-slate-400 text-xs font-medium font-['Plus_Jakarta_Sans'] leading-4">
                  <div className="w-48 text-left">{t("opponent", "Opponent")}</div>
                  <div className="w-40 text-left">{t("dealType", "Deal type")}</div>
                  <div className="w-36 text-left">{t("sport", "Sport")}</div>
                  <div className="w-36 text-left">{t("weightClass", "Weight Class")}</div>
                  <div className="w-60 text-left">{t("location", "Location")}</div>
                  <div className="w-36 text-left">{t("date", "Date")}</div>
                  <div className="w-36 text-left">Status</div>
                  <div className="flex-1 text-right"></div>
                </div>
                <div className="min-w-[1000px] bg-white rounded-2xl flex flex-col divide-y divide-slate-100 shadow-sm overflow-hidden">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="min-h-14 px-4 py-3 flex items-center gap-6">
                      <div className="w-48 flex items-center gap-3">
                        <div className="size-8 shrink-0 rounded-full bg-slate-200" />
                        <div className="space-y-1.5 flex-1">
                          <div className="h-3.5 w-3/4 bg-slate-200 rounded" />
                          <div className="h-2.5 w-1/2 bg-slate-100 rounded" />
                        </div>
                      </div>
                      <div className="w-40"><div className="h-3.5 w-24 bg-slate-200 rounded" /></div>
                      <div className="w-36"><div className="h-3.5 w-20 bg-slate-200 rounded" /></div>
                      <div className="w-36"><div className="h-3.5 w-20 bg-slate-200 rounded" /></div>
                      <div className="w-60"><div className="h-3.5 w-32 bg-slate-200 rounded" /></div>
                      <div className="w-36"><div className="h-3.5 w-20 bg-slate-200 rounded" /></div>
                      <div className="w-36"><div className="h-5 w-20 bg-slate-200 rounded-xl" /></div>
                      <div className="flex-1 flex justify-end"><div className="size-8 rounded-2xl bg-slate-100" /></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {error && (
          <div className="text-center py-8 bg-red-50 rounded-2xl border border-red-200">
            <p className="text-xs text-red-700">{error}</p>
          </div>
        )}

        {!loading && !error && filteredDeals.length === 0 && (
          <div className="p-1 bg-[#f4f5f7] rounded-[20px] border border-slate-200/80 overflow-hidden shadow-sm">
            <div className="w-full overflow-x-auto">
              <div className="min-w-[1000px] px-4 py-2.5 flex items-center gap-6 text-slate-400 text-xs font-medium font-['Plus_Jakarta_Sans'] leading-4">
                <div className="w-48 text-left">{t("opponent", "Opponent")}</div>
                <div className="w-40 text-left">{t("dealType", "Deal type")}</div>
                <div className="w-36 text-left">{t("sport", "Sport")}</div>
                <div className="w-36 text-left">{t("weightClass", "Weight Class")}</div>
                <div className="w-60 text-left">{t("location", "Location")}</div>
                <div className="w-36 text-left">{t("date", "Date")}</div>
                <div className="w-36 text-left">Status</div>
                <div className="flex-1 text-right"></div>
              </div>

              <div className="min-w-[1000px] bg-white rounded-2xl flex flex-col items-center justify-center py-24 px-4 shadow-sm">
                <img
                  src={noDealsEmptyIcon}
                  alt="No deals"
                  className="w-20 h-20 mb-3 object-contain"
                />
                <p className="text-sm font-normal text-slate-500 font-['Plus_Jakarta_Sans']">
                  No deals to display
                </p>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && filteredDeals.length > 0 && (
          <>
            <div className="lg:hidden flex flex-col gap-4">
              {filteredDeals.map((deal) => {
                const displayProfile =
                  role === "FIGHTER" ? deal.initiatedBy : deal.acceptedBy;
                const opponentName = displayProfile
                  ? `${displayProfile.firstName ?? ""} ${displayProfile.lastName ?? ""}`.trim() ||
                    displayProfile.username ||
                    t("unknownUser", "Opponent")
                  : t("unknownUser", "Opponent");
                const opponentSub =
                  (displayProfile as { email?: string })?.email ||
                  displayProfile?.username ||
                  "";

                return (
                  <div
                    key={deal.id}
                    className="bg-white rounded-3xl p-4 border border-slate-200/80 shadow-[0_0_1px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.06)] flex flex-col gap-3 font-['Plus_Jakarta_Sans']"
                  >
                    <div className="flex flex-col gap-1.5">
                      <span className="text-xs text-slate-400 font-normal">
                        {t("opponent", "Opponent")}
                      </span>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <button
                            type="button"
                            onClick={() => navigateToProfile(displayProfile?.id)}
                            disabled={!displayProfile?.id}
                            className="size-10 shrink-0 rounded-full overflow-hidden bg-gradient-to-tr from-amber-400 via-orange-400 to-amber-500 shadow-sm flex items-center justify-center text-white text-xs font-semibold"
                          >
                            {displayProfile?.profileImage ? (
                              <img
                                src={displayProfile.profileImage}
                                alt={opponentName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <UserIcon className="w-5 h-5 text-white" />
                            )}
                          </button>
                          <div className="min-w-0 flex-1 text-left">
                            <button
                              type="button"
                              onClick={() => navigateToProfile(displayProfile?.id)}
                              disabled={!displayProfile?.id}
                              className="text-slate-900 text-sm font-bold leading-5 truncate hover:underline block text-left"
                            >
                              {opponentName}
                            </button>
                            {opponentSub && (
                              <p className="text-slate-400 text-xs font-normal leading-4 truncate">
                                {opponentSub}
                              </p>
                            )}
                          </div>
                        </div>

                        <span
                          className={`h-6 px-2.5 py-0.5 rounded-xl text-xs font-medium inline-flex items-center shrink-0 ${getStatusBadgeStyle(
                            deal.status,
                          )}`}
                        >
                          {getStatusLabel(deal.status || "")}
                        </span>
                      </div>
                    </div>

                    <div className="border-t border-slate-100 pt-2.5 flex items-center justify-between text-left">
                      <span className="text-xs text-slate-400 font-normal">
                        {t("dealType", "Deal type")}
                      </span>
                      <span className="text-sm text-slate-900 font-semibold truncate ml-2">
                        {formatDealTypeLabel(deal.dealType)}
                      </span>
                    </div>

                    <div className="border-t border-slate-100 pt-2.5 flex items-center justify-between text-left">
                      <span className="text-xs text-slate-400 font-normal">
                        {t("sport", "Sport")}
                      </span>
                      <span className="text-sm text-slate-900 font-semibold truncate capitalize ml-2">
                        {formatSportName(getSport(deal))}
                      </span>
                    </div>

                    <div className="border-t border-slate-100 pt-2.5 flex items-center justify-between text-left">
                      <span className="text-xs text-slate-400 font-normal">
                        {t("weightClass", "Weight Class")}
                      </span>
                      <span className="text-sm text-slate-900 font-semibold truncate capitalize ml-2">
                        {formatWeightClassName(getWeightClass(deal))}
                      </span>
                    </div>

                    <div className="border-t border-slate-100 pt-2.5 flex items-center justify-between text-left">
                      <span className="text-xs text-slate-400 font-normal">
                        {t("location", "Location")}
                      </span>
                      <span className="text-sm text-slate-900 font-semibold truncate ml-2">
                        {getLocation(deal)}
                      </span>
                    </div>

                    <div className="border-t border-slate-100 pt-2.5 flex items-center justify-between text-left">
                      <span className="text-xs text-slate-400 font-normal">
                        {t("date", "Date")}
                      </span>
                      <span className="text-sm text-slate-900 font-semibold truncate ml-2">
                        {getEventDate(deal) ? formatDate(getEventDate(deal)) : "—"}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => navigate(`/deal/view/${deal.id}`)}
                      className="w-full mt-1 h-10 bg-slate-100 hover:bg-slate-200 rounded-2xl flex items-center justify-center gap-2 text-sm font-medium text-slate-700 transition-colors cursor-pointer"
                      title="View deal"
                    >
                      <EyeIcon className="w-4 h-4 text-slate-700" />
                      <span>{t("view", "View")}</span>
                    </button>
                  </div>
                );
              })}

            </div>

            <div className="hidden lg:block p-1 bg-[#f4f5f7] rounded-[20px] border border-slate-200/80 overflow-hidden shadow-sm">
              <div className="w-full overflow-x-auto">
                <div className="min-w-[1000px] px-4 py-2.5 flex items-center gap-6 text-slate-400 text-xs font-medium font-['Plus_Jakarta_Sans'] leading-4">
                  <div className="w-48 text-left">{t("opponent", "Opponent")}</div>
                  <div className="w-40 text-left">{t("dealType", "Deal type")}</div>
                  <div className="w-36 text-left">{t("sport", "Sport")}</div>
                  <div className="w-36 text-left">{t("weightClass", "Weight Class")}</div>
                  <div className="w-60 text-left">{t("location", "Location")}</div>
                  <div className="w-36 text-left">{t("date", "Date")}</div>
                  <div className="w-36 text-left">Status</div>
                  <div className="flex-1 text-right"></div>
                </div>

                <div className="min-w-[1000px] bg-white rounded-2xl flex flex-col divide-y divide-slate-100 shadow-sm overflow-hidden">
                  {filteredDeals.map((deal) => {
                    const displayProfile =
                      role === "FIGHTER" ? deal.initiatedBy : deal.acceptedBy;
                    const opponentName = displayProfile
                      ? `${displayProfile.firstName ?? ""} ${displayProfile.lastName ?? ""}`.trim() ||
                        displayProfile.username ||
                        t("unknownUser", "Opponent")
                      : t("unknownUser", "Opponent");
                    const opponentSub =
                      (displayProfile as { email?: string })?.email ||
                      displayProfile?.username ||
                      "";

                    return (
                      <div key={deal.id} className="min-h-14 px-4 py-3 flex items-center gap-6 hover:bg-slate-50/70 transition-colors">
                        <div className="w-48 flex items-center gap-3 min-w-0">
                          <button
                            type="button"
                            onClick={() => navigateToProfile(displayProfile?.id)}
                            disabled={!displayProfile?.id}
                            className="size-8 shrink-0 rounded-3xl overflow-hidden bg-gradient-to-tr from-amber-400 via-orange-400 to-amber-500 shadow-sm flex items-center justify-center text-white text-xs font-semibold"
                          >
                            {displayProfile?.profileImage ? (
                              <img
                                src={displayProfile.profileImage}
                                alt={opponentName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <UserIcon className="w-4 h-4 text-white" />
                            )}
                          </button>
                          <div className="min-w-0 flex-1 text-left">
                            <button
                              type="button"
                              onClick={() => navigateToProfile(displayProfile?.id)}
                              disabled={!displayProfile?.id}
                              className="text-slate-900 text-sm font-medium font-['Plus_Jakarta_Sans'] leading-5 truncate hover:underline block text-left"
                            >
                              {opponentName}
                            </button>
                            {opponentSub && (
                              <p className="text-slate-400 text-xs font-normal font-['Plus_Jakarta_Sans'] leading-4 truncate">
                                {opponentSub}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="w-40 text-left truncate text-slate-900 text-sm font-medium font-['Plus_Jakarta_Sans'] leading-5">
                          {formatDealTypeLabel(deal.dealType)}
                        </div>

                        <div className="w-36 text-left truncate text-slate-900 text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5 capitalize">
                          {formatSportName(getSport(deal))}
                        </div>

                        <div className="w-36 text-left truncate text-slate-900 text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5 capitalize">
                          {formatWeightClassName(getWeightClass(deal))}
                        </div>

                        <div className="w-60 text-left truncate text-slate-900 text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5">
                          {getLocation(deal)}
                        </div>

                        <div className="w-36 text-left truncate text-slate-900 text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5">
                          {getEventDate(deal) ? formatDate(getEventDate(deal)) : "—"}
                        </div>

                        <div className="w-36 flex items-center justify-start">
                          <span
                            className={`h-5 px-2 py-0.5 rounded-xl text-xs font-medium font-['Plus_Jakarta_Sans'] leading-4 inline-flex items-center ${getStatusBadgeStyle(
                              deal.status,
                            )}`}
                          >
                            {getStatusLabel(deal.status || "")}
                          </span>
                        </div>

                        <div className="flex-1 flex justify-end items-center">
                          <button
                            type="button"
                            onClick={() => navigate(`/deal/view/${deal.id}`)}
                            className="size-8 min-w-8 p-1.5 bg-slate-100 hover:bg-slate-200 rounded-2xl flex justify-center items-center transition-all cursor-pointer text-slate-700"
                            title="View deal"
                          >
                            <EyeIcon className="w-4 h-4 text-slate-700" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}

        {filteredDeals.length > 0 && hasMore && (
          <div ref={observerRef} className="w-full flex items-center justify-center py-6">
            {loadingMore && <Spinner size="md" color="primary" />}
          </div>
        )}
      </div>
    </div>
  );
};

export default DealAccordion;
